import { useEffect, useState } from 'react';
import { X, Lock, AlertTriangle, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { listConferimenti } from '../../api/conferimenti';
import type { ConferimentoListItem } from '../../api/conferimenti';
import { upsertChiusura } from '../../api/chiusure';
import type { Chiusura } from '../../api/chiusure';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { canWriteProgrammazione } from '../../lib/constants';

interface Props {
  data: string;
  chiusuraEsistente: Chiusura | null;
  onClose: () => void;
  onChiusuraUpdated: () => void;
  onProgrammaNuovo: (data: string) => void;
}

export default function GestioneGiornoModal({ data, chiusuraEsistente, onClose, onChiusuraUpdated, onProgrammaNuovo }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const canEditChiusure = canWriteProgrammazione(user?.role);

  const [conferimenti, setConferimenti] = useState<ConferimentoListItem[]>([]);
  const [loadingConf, setLoadingConf] = useState(true);

  const [impiantoChiuso, setImpiantoChiuso] = useState(chiusuraEsistente?.impianto_chiuso ?? false);
  const [ricezioneChiusa, setRicezioneChiusa] = useState(chiusuraEsistente?.ricezione_chiusa ?? false);
  const [lavorazioneChiusa, setLavorazioneChiusa] = useState(chiusuraEsistente?.lavorazione_chiusa ?? false);
  const [note, setNote] = useState(chiusuraEsistente?.note ?? '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const dataFormatted = new Date(data + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  useEffect(() => {
    listConferimenti({ data_da: data, data_a: data, limit: 50 })
      .then(res => setConferimenti(res.items))
      .catch(() => {})
      .finally(() => setLoadingConf(false));
  }, [data]);

  async function handleSaveChiusure() {
    setSaving(true);
    setSaveError(null);
    try {
      await upsertChiusura(data, {
        impianto_chiuso: impiantoChiuso,
        ricezione_chiusa: ricezioneChiusa,
        lavorazione_chiusa: lavorazioneChiusa,
        note: note || null,
      });
      onChiusuraUpdated();
      onClose();
    } catch {
      setSaveError(t('programmazione.giorno_modal.errore_salvataggio'));
    } finally {
      setSaving(false);
    }
  }

  const totalePeso = conferimenti.reduce((acc, c) => acc + (c.peso_stimato_kg ?? 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-mid sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-semibold text-ink-dark capitalize">{dataFormatted}</h2>
            {(chiusuraEsistente?.impianto_chiuso || chiusuraEsistente?.ricezione_chiusa) && (
              <div className="flex items-center gap-1 mt-0.5 text-xs text-amber-600">
                <AlertTriangle size={11} />
                {chiusuraEsistente.impianto_chiuso ? t('programmazione.giorno_modal.impianto_chiuso') : t('programmazione.giorno_modal.ricezione_chiusa')}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark"><X size={18} /></button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Conferimenti del giorno */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-ink-dark flex items-center gap-1.5">
                <Package size={14} className="text-sage" />
                {t('programmazione.giorno_modal.conferimenti', { n: conferimenti.length })}
              </h3>
              {totalePeso > 0 && (
                <span className="text-xs text-ink-soft">{t('programmazione.giorno_modal.tot_totali', { t: (totalePeso / 1000).toFixed(1) })}</span>
              )}
            </div>

            {loadingConf ? (
              <div className="text-xs text-ink-soft py-2">{t('programmazione.giorno_modal.caricamento')}</div>
            ) : conferimenti.length === 0 ? (
              <div className="text-xs text-ink-faint py-2 text-center border border-dashed border-stone-border rounded-lg">{t('programmazione.giorno_modal.vuoto')}</div>
            ) : (
              <div className="space-y-1.5">
                {conferimenti.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => navigate(`/conferimenti/${c.id}`)}
                    className="w-full text-left p-2.5 bg-stone-bg border border-stone-border rounded-lg hover:border-sage transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs font-semibold text-ink-dark">{c.numero ?? '—'}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        c.status_display === 'programmato' ? 'bg-blue-100 text-blue-700' :
                        c.status_display === 'in_ritardo' ? 'bg-red-100 text-red-700' :
                        c.status_display === 'in_stoccaggio' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>{t(`conferimenti.card.stato.${c.status_display}`, { defaultValue: c.status_display })}</span>
                    </div>
                    <div className="text-xs text-ink-soft mt-0.5">
                      {c.customer_ragione_sociale} · {c.peso_stimato_kg ? `${(c.peso_stimato_kg / 1000).toFixed(1)} t` : '—'}
                      {c.area_tentativa_codice && ` · ${t('conferimenti.card.area', { code: c.area_tentativa_codice })}`}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Chiusure (solo admin/AMM) */}
          {canEditChiusure && (
            <div className="border-t border-stone-mid pt-4">
              <h3 className="text-sm font-semibold text-ink-dark mb-3 flex items-center gap-1.5">
                <Lock size={14} className="text-ink-mid" />
                {t('programmazione.giorno_modal.chiusure')}
              </h3>
              <div className="space-y-2">
                {[
                  { key: 'impianto', label: t('programmazione.giorno_modal.impianto_chiuso'), value: impiantoChiuso, set: setImpiantoChiuso },
                  { key: 'ricezione', label: t('programmazione.giorno_modal.ricezione_chiusa'), value: ricezioneChiusa, set: setRicezioneChiusa },
                  { key: 'lavorazione', label: t('programmazione.giorno_modal.lavorazione_chiusa'), value: lavorazioneChiusa, set: setLavorazioneChiusa },
                ].map(item => (
                  <label key={item.key} className="flex items-center gap-2.5 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={item.value}
                      onChange={e => item.set(e.target.checked)}
                      className="w-4 h-4 rounded border-stone-border text-sage focus:ring-sage"
                    />
                    <span className="text-sm text-ink-dark group-hover:text-ink-dark">{item.label}</span>
                  </label>
                ))}
                <div className="mt-2">
                  <label className="block text-xs font-medium text-ink-soft mb-1">{t('programmazione.giorno_modal.note')}</label>
                  <textarea
                    rows={2}
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder={t('programmazione.giorno_modal.note_placeholder')}
                    className="w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white resize-none"
                  />
                </div>
                {saveError && (
                  <div className="text-xs text-accent-danger px-2 py-1 bg-accent-danger-bg border border-[#FECACA] rounded">{saveError}</div>
                )}
                <button
                  type="button"
                  onClick={handleSaveChiusure}
                  disabled={saving}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60"
                >
                  {saving ? t('programmazione.giorno_modal.salvataggio') : t('programmazione.giorno_modal.salva_chiusure')}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-stone-mid bg-stone-bg">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors">
            {t('programmazione.giorno_modal.chiudi')}
          </button>
          <button
            type="button"
            onClick={() => onProgrammaNuovo(data)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors"
          >
            {t('programmazione.giorno_modal.programma_nuovo')}
          </button>
        </div>
      </div>
    </div>
  );
}
